// src/pages/HorenTrainingPage.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ArrowLeft, ChevronRight, Play, Pause, RotateCcw,
  CheckCircle, XCircle, BookOpen, Layers, Home,
  Headphones, AlertCircle, Lightbulb, ArrowUpRight, Volume2, ClipboardList, FileText
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import PartDisplay from '../components/practice/PartDisplay';
import InformationTransferTable from '../components/practice/tasks/InformationTransferTable';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';
import PracticeAudioPlayer from '../components/audio/PracticeAudioPlayer';
import HorenTranscriptBody from '../components/audio/HorenTranscript';
import SlideOutPanel from '../components/dashboard/SlideOutPanel';
import PracticeHistoryPanel from '../components/practice/PracticeHistoryPanel';
import SampleSelectView from '../components/practice/SampleSelectView';
import ExplanationPanel from '../components/practice/ExplanationPanel';
import adminService from '../services/adminService';
import subscriptionService from '../services/subscriptionService';
import enhancedUserService from '../services/enhancedUserService';
import { gradeListeningPart, normalizeTF, isTFCorrect } from '../utils/practiceGrading';
import { readSession, writeSession } from '../utils/sessionStore';
import freeAttemptService from '../services/freeAttemptService';
import { auth } from '../firebase';
import storageService from '../services/storageService';
import { marked } from 'marked';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';
import AhaUpsellModal from '../components/ui/AhaUpsellModal';
import { trackPaywallShown } from '../utils/conversionEvents';

// ─── PART TYPE META ─────────────────────────────────────────────────────────────
const PART_ICON_COMPONENTS = {
  'short-texts':               Headphones,
  'short-texts-announcements': Headphones,
  'short-texts-opinion':       Headphones,
  'conversation':              Headphones,
  'conversation-comprehension': Headphones,
  'radio-interview':           Headphones,
  'news-broadcast':            Headphones,
  'monologue-comprehension':   Headphones,
  'discussion-comprehension':  Layers,
  'matching-opinions':         Layers,
  'information-transfer-notes': BookOpen,
  'fill-blank':                BookOpen,
};

const PART_TYPE_LABELS = {
  'short-texts':               'Kurztexte · Richtig/Falsch',
  'short-texts-announcements': 'Durchsagen · Richtig/Falsch',
  'short-texts-opinion':       'Meinungen · Richtig/Falsch',
  'conversation':              'Gespräch · Richtig/Falsch',
  'conversation-comprehension': 'Gespräch · Richtig/Falsch',
  'radio-interview':           'Radio-Interview · Richtig/Falsch',
  'news-broadcast':            'Nachrichtensendung · Richtig/Falsch',
  'monologue-comprehension':   'Monolog · Multiple Choice',
  'discussion-comprehension':  'Diskussion · Sprecher zuordnen',
  'matching-opinions':         'Meinungen · Aussagen zuordnen',
  'information-transfer-notes': 'Notizen ergänzen',
  'fill-blank':                'Lücken ergänzen',
};

const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';
const SOFT_SURFACE =
  'rounded-2xl border border-black/10 bg-white/95 backdrop-blur-sm shadow-[0_4px_14px_rgba(15,23,42,0.05)]';

// Task type sets for AnimatedTaskTypeIcon decoration variants
const AUDIO_TASK_TYPES = new Set([
  'short-texts', 'short-texts-announcements', 'short-texts-opinion',
  'conversation', 'conversation-comprehension', 'radio-interview',
  'news-broadcast', 'monologue-comprehension',
]);
const MATCHING_TASK_TYPES = new Set([
  'discussion-comprehension', 'matching-opinions',
]);
const NOTES_TASK_TYPES = new Set([
  'information-transfer-notes', 'fill-blank',
]);

const HOREN_TRAINING_CARD_STYLES = `
  @keyframes horenCardFloat {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50% { transform: translate3d(0, -3px, 0); }
  }
  @keyframes horenCardPulse {
    0%, 100% { opacity: 0.55; transform: scale(0.96); }
    50% { opacity: 0.95; transform: scale(1.08); }
  }
  @keyframes horenCardSweep {
    0%, 100% { opacity: 0.4; transform: translateX(0) scaleX(0.92); }
    50% { opacity: 0.95; transform: translateX(3px) scaleX(1); }
  }
  @keyframes horenCardStack {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-2px); }
  }
  .horen-task-card:hover .horen-task-icon-shell { transform: translateY(-2px); }
  .horen-task-card:hover .horen-task-icon-core { transform: scale(1.04); }
  .horen-task-icon-shell,
  .horen-task-icon-core,
  .horen-task-icon-arrow {
    transition: transform 220ms ease, color 220ms ease, box-shadow 220ms ease;
  }
  .horen-task-icon-orb { animation: horenCardFloat 3.8s ease-in-out infinite; }
  .horen-task-icon-wave { animation: horenCardSweep 2.8s ease-in-out infinite; transform-origin: left center; }
  .horen-task-icon-chip { animation: horenCardPulse 2.8s ease-in-out infinite; }
  .horen-task-icon-stack { animation: horenCardStack 3s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .horen-task-icon-orb,
    .horen-task-icon-wave,
    .horen-task-icon-chip,
    .horen-task-icon-stack { animation: none; }
    .horen-task-card:hover .horen-task-icon-shell,
    .horen-task-card:hover .horen-task-icon-core { transform: none; }
  }
`;

function AnimatedBackButton({ onClick, compact = false, backLabel = 'Back' }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-xl border border-[#E1E4E8] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-lg touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35 ${
        compact ? 'min-h-[44px] min-w-[44px] p-2.5' : 'min-h-[48px] px-3.5 py-2.5'
      }`}
      aria-label={backLabel}
      type="button"
    >
      <div
        style={{
          position: 'absolute', top: 0, bottom: 0, right: 0,
          width: isHovered ? '100%' : '0%',
          background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
          transition: 'width 0.7s ease-out', zIndex: 0,
        }}
      />
      <ArrowLeft className="w-4 h-4 relative z-10 transition-colors duration-500" style={{ color: isHovered ? '#ffffff' : '#000000' }} />
      {!compact && (
        <span className="hidden sm:inline font-semibold text-sm relative z-10 transition-colors duration-500" style={{ color: isHovered ? '#ffffff' : '#000000' }}>
          {backLabel}
        </span>
      )}
    </button>
  );
}

function AnimatedTaskTypeIcon({ taskType }) {
  const Icon = PART_ICON_COMPONENTS[taskType] || Headphones;
  const shellClasses = 'border-[#3A86FF]/18 bg-gradient-to-br from-[#EEF5FF] via-white to-[#F6F8FC] shadow-[0_10px_20px_rgba(58,134,255,0.12)]';
  const glowClasses = 'from-[#3A86FF]/14 via-white/90 to-transparent';
  const iconClasses = 'text-[#275eb4]';
  const accentFill = 'bg-[#3A86FF]/14';
  const accentBorder = 'border-[#3A86FF]/18';
  const lineClasses = 'bg-[#3A86FF]/35';

  return (
    <div className={`horen-task-icon-shell relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-[18px] border ${shellClasses}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${glowClasses}`} />
      <div className={`horen-task-icon-orb absolute -right-1 -top-1 h-4 w-4 rounded-full border bg-white/90 ${accentBorder}`} />

      {AUDIO_TASK_TYPES.has(taskType) && (
        <div className="absolute bottom-2.5 left-2.5 right-2.5 z-0 space-y-1">
          <span className={`horen-task-icon-wave block h-[2px] w-5 rounded-full ${lineClasses}`} />
          <span className={`horen-task-icon-wave block h-[2px] w-7 rounded-full ${lineClasses}`} style={{ animationDelay: '120ms' }} />
          <span className={`horen-task-icon-wave block h-[2px] w-4 rounded-full ${lineClasses}`} style={{ animationDelay: '240ms' }} />
        </div>
      )}

      {MATCHING_TASK_TYPES.has(taskType) && (
        <div className="absolute bottom-2.5 left-2.5 z-0">
          <span className="block -rotate-[7deg]">
            <span className={`horen-task-icon-stack block h-2.5 w-5 rounded-[6px] border ${accentFill} ${accentBorder}`} />
          </span>
          <span className="absolute left-1 top-1 block rotate-[5deg]">
            <span className={`horen-task-icon-stack block h-2.5 w-5 rounded-[6px] border ${accentFill} ${accentBorder}`} style={{ animationDelay: '180ms' }} />
          </span>
        </div>
      )}

      {NOTES_TASK_TYPES.has(taskType) && (
        <div className="absolute bottom-2.5 left-2.5 right-2.5 z-0 flex items-center gap-1.5">
          <span className={`horen-task-icon-chip h-2 w-2 rounded-full ${accentFill}`} />
          <span className={`horen-task-icon-wave block h-[2px] w-4 rounded-full ${lineClasses}`} style={{ animationDelay: '100ms' }} />
          <span className={`horen-task-icon-wave block h-[2px] w-2.5 rounded-full ${lineClasses}`} style={{ animationDelay: '220ms' }} />
        </div>
      )}

      <div className="horen-task-icon-core relative z-10 flex items-center justify-center">
        <Icon className={`h-6 w-6 ${iconClasses}`} />
      </div>
    </div>
  );
}

// ─── RESULT BADGE ──────────────────────────────────────────────────────────────
function ResultBadge({ userAnswer, correctAnswer, isCorrect }) {
  if (isCorrect) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-100 border border-emerald-400 font-black text-emerald-900 text-xs sm:text-sm shadow-[0_4px_12px_rgba(16,185,129,0.2)]">
        <CheckCircle className="w-3.5 h-3.5" />
        <span className="tracking-wide uppercase text-[10px]">Richtig</span>
        <span>{String(userAnswer || '').toUpperCase()}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 sm:gap-2 flex-wrap">
      {userAnswer != null && userAnswer !== '' ? (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 border border-rose-300 font-black text-rose-900 text-xs sm:text-sm">
          <XCircle className="w-3.5 h-3.5" />
          {String(userAnswer).toUpperCase()}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 border border-gray-300 font-bold text-gray-500 text-xs sm:text-sm">Keine Antwort</span>
      )}
      <span className="text-[11px] font-bold text-gray-400">-&gt;</span>
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 border border-emerald-400 font-black text-emerald-900 text-xs sm:text-sm">
        <CheckCircle className="w-3.5 h-3.5" />
        {String(correctAnswer || '').toUpperCase()}
      </span>
    </span>
  );
}


// ─── TRUE/FALSE DISPLAY (normalization lives in ../utils/practiceGrading) ──────
function displayTF(v) {
  const n = normalizeTF(v);
  if (n === 'richtig') return 'Richtig';
  if (n === 'falsch')  return 'Falsch';
  return v != null ? String(v) : ', ';
}

// ─── RESULTS: STANDARD LIST (true-false + multiple-choice) ────────────────────
function StandardListResult({ part, answers }) {
  const qs = (part.questions || []).filter(q => !q.isExample);
  const qt = part.questionType;

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
      {qs.map(q => {
        // Determine question type per-question (for mixed type)
        const qType = q.questionType || qt;
        const userAnswer = answers[q.questionId];
        const label = q.questionText || q.statement || '';

        let isCorrect;
        let displayAnswer;
        let displayCorrect;

        if (qType === 'multiple-choice') {
          isCorrect = userAnswer === q.correctAnswer;
          displayAnswer = userAnswer;
          displayCorrect = q.correctAnswer;
        } else {
          // true-false (any encoding)
          isCorrect = isTFCorrect(userAnswer, q.correctAnswer);
          displayAnswer = userAnswer != null ? displayTF(userAnswer) : null;
          displayCorrect = displayTF(q.correctAnswer);
        }

        return (
          <div
            key={q.questionId}
            className={`rounded-2xl overflow-hidden border-2 shadow-[0_10px_20px_rgba(15,23,42,0.06)] ${isCorrect ? 'border-emerald-400' : 'border-rose-300'}`}
          >
            <div className={`px-4 py-2.5 flex items-start justify-between gap-3 ${isCorrect ? 'bg-emerald-50/80' : 'bg-rose-50/80'}`}>
              <p className="font-semibold text-gray-900 text-sm flex-1">
                <span className="font-black mr-2">{q.questionNumber}.</span>
                {label}
              </p>
              <div className="flex-shrink-0">
                {qType === 'multiple-choice' ? (
                  <ResultBadge userAnswer={displayAnswer} correctAnswer={displayCorrect} isCorrect={isCorrect} />
                ) : (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border font-bold text-xs ${
                    isCorrect
                      ? 'bg-emerald-100 border-emerald-400 text-emerald-900'
                      : 'bg-rose-50 border-rose-300 text-rose-900'
                  }`}>
                    {isCorrect ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {displayAnswer || ', '}
                    {!isCorrect && <span className="text-gray-400 mx-1">→</span>}
                    {!isCorrect && <span className="text-emerald-700">{displayCorrect}</span>}
                  </span>
                )}
              </div>
            </div>

            {qType === 'multiple-choice' && (
              <div className="bg-white p-4 space-y-2">
                {(q.options || []).map(opt => {
                  const isThisCorrect  = opt.id === q.correctAnswer;
                  const isThisSelected = opt.id === userAnswer;
                  let cls = 'flex items-start gap-3 p-3 rounded border text-sm ';
                  if (isThisCorrect)                     cls += 'border-emerald-400 bg-emerald-100 text-emerald-950 font-semibold';
                  else if (isThisSelected && !isCorrect) cls += 'border-rose-300 bg-rose-50 text-rose-900';
                  else                                   cls += 'border-black/15 bg-white text-gray-600';
                  return (
                    <div key={opt.id} className={cls}>
                      <span className="font-black text-xs mt-0.5 min-w-[16px]">{opt.id.toUpperCase()}</span>
                      <span className="flex-1">{opt.text}</span>
                      {isThisCorrect && <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
                      {isThisSelected && !isCorrect && <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />}
                    </div>
                  );
                })}
                <ExplanationPanel explanation={q.explanation} />
              </div>
            )}

            {qType !== 'multiple-choice' && (
              <div className="bg-white px-4 pb-3">
                <ExplanationPanel explanation={q.explanation} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── RESULTS: MATCHING (opinions pool or speaker matching) ────────────────────
function MatchingResult({ part, answers }) {
  const qs = (part.questions || []).filter(q => !q.isExample);
  const options = part.content?.options || [];
  // For discussion-comprehension, speakers come from q.speakers
  const speakersFromQ = qs[0]?.speakers || [];

  // Build lookup: option id → text
  const optionMap = options.reduce((acc, o) => { acc[o.id] = o.text; return acc; }, {});

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Options pool (matching-opinions style) */}
      {options.length > 0 && (
        <div className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
          <h3 className="text-base font-bold text-gray-900 border-b border-black/10 pb-2 mb-3">Aussagen</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {options.map(o => (
              <li key={o.id} className="text-sm flex gap-2">
                <span className="font-black min-w-[20px]">{o.id.toUpperCase()})</span>
                <span className="text-gray-700">{o.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Speakers list (discussion-comprehension style) */}
      {speakersFromQ.length > 0 && options.length === 0 && (
        <div className={`${ELEVATED_SURFACE} p-4 animate-fade-in-up`}>
          <h3 className="text-sm font-bold text-gray-900 mb-2">Sprecher</h3>
          <div className="flex flex-wrap gap-2">
            {speakersFromQ.map(s => (
              <span key={s} className="px-3 py-1 border border-black/10 bg-gray-50 text-sm font-semibold text-gray-700 rounded-xl">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-3">
        {qs.map(q => {
          const userAnswer = answers[q.questionId];
          const isCorrect  = userAnswer === q.correctAnswer;
          const label = q.questionText || q.statement || `Sprecher ${q.questionNumber}`;

          // Display text for option-based matching
          const userDisplay   = options.length > 0 ? `${userAnswer?.toUpperCase()}) ${optionMap[userAnswer] || ''}` : userAnswer;
          const correctDisplay = options.length > 0 ? `${q.correctAnswer?.toUpperCase()}) ${optionMap[q.correctAnswer] || ''}` : q.correctAnswer;

          return (
            <div
              key={q.questionId}
              className={`rounded-2xl overflow-hidden border-2 shadow-[0_10px_22px_rgba(15,23,42,0.07)] ${isCorrect ? 'border-emerald-400' : 'border-rose-300'}`}
            >
              <div className={`flex items-start justify-between gap-3 px-4 py-3 ${isCorrect ? 'bg-emerald-50/80' : 'bg-rose-50/80'}`}>
                <p className="font-semibold text-gray-900 text-sm flex-1">
                  <span className="font-black mr-2">{q.questionNumber}.</span>
                  {label}
                </p>
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border font-bold text-xs ${
                    isCorrect ? 'bg-emerald-100 border-emerald-400 text-emerald-900' : 'bg-rose-50 border-rose-300 text-rose-900'
                  }`}>
                    {isCorrect ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {userAnswer || ', '}
                  </span>
                </div>
              </div>
              {!isCorrect && (
                <div className="bg-white px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Deine Antwort: <span className="font-semibold text-rose-700">{userDisplay || ', '}</span>
                    <span className="mx-2 text-gray-300">→</span>
                    Richtig: <span className="font-semibold text-emerald-700">{correctDisplay}</span>
                  </p>
                  <ExplanationPanel explanation={q.explanation} />
                </div>
              )}
              {isCorrect && (
                <div className="bg-white px-4 pb-3">
                  <ExplanationPanel explanation={q.explanation} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULTS: FILL BLANK (information-transfer-notes) ─────────────────────────
function FillBlankResult({ part, answers }) {
  const rawText  = part.content?.mainText || '';
  const qs       = (part.questions || []).filter(q => !q.isExample);
  const qByNum   = qs.reduce((acc, q) => { acc[q.blankNumber] = q; return acc; }, {});

  // Parse [65] style placeholders
  const segments = [];
  const re = /\[(\d+)\]/g;
  let lastIdx = 0;
  let match;
  while ((match = re.exec(rawText)) !== null) {
    if (match.index > lastIdx) segments.push({ type: 'text', value: rawText.slice(lastIdx, match.index) });
    segments.push({ type: 'blank', blankNumber: parseInt(match[1], 10) });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < rawText.length) segments.push({ type: 'text', value: rawText.slice(lastIdx) });

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Template with answers filled in */}
      {rawText && (
        <div className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
          <p className="text-sm text-gray-800 leading-loose whitespace-pre-wrap font-medium">
            {segments.map((seg, i) => {
              if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
              const q = qByNum[seg.blankNumber];
              if (!q) return <span key={i} className="font-bold text-gray-400">[{seg.blankNumber}]</span>;
              const userAnswer = answers[q.questionId];
              const correctArr = Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer];
              const isCorrect  = correctArr.some(ca =>
                String(userAnswer || '').trim().toLowerCase() === String(ca).trim().toLowerCase()
              );
              return (
                <span key={i} className="inline-flex items-center mx-0.5">
                  {userAnswer ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold text-xs border ${
                      isCorrect ? 'bg-emerald-100 border-emerald-400 text-emerald-900'
                                : 'bg-rose-50 border-rose-300 text-rose-900'
                    }`}>
                      {isCorrect ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {userAnswer}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold text-xs border bg-gray-100 border-gray-300 text-gray-500">[{seg.blankNumber}]</span>
                  )}
                  {!isCorrect && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 ml-1 rounded font-bold text-xs border bg-emerald-100 border-emerald-400 text-emerald-900">
                      <CheckCircle className="w-3 h-3" />
                      {correctArr[0]}
                    </span>
                  )}
                </span>
              );
            })}
          </p>
        </div>
      )}

      {/* Per-blank breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {qs.map(q => {
          const userAnswer = answers[q.questionId];
          const correctArr = Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer];
          const isCorrect  = correctArr.some(ca =>
            String(userAnswer || '').trim().toLowerCase() === String(ca).trim().toLowerCase()
          );
          return (
            <div key={q.questionId} className={`rounded-2xl border-2 p-3 shadow-[0_8px_18px_rgba(15,23,42,0.05)] ${isCorrect ? 'border-emerald-400 bg-emerald-50/60' : 'border-rose-300 bg-rose-50/60'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-black text-gray-900 text-sm">({q.blankNumber})</span>
                <span className={`text-xs font-bold ${isCorrect ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {isCorrect ? 'Richtig' : 'Falsch'}
                </span>
              </div>
              {userAnswer && (
                <p className="text-xs text-gray-600 mb-1">
                  Deine Antwort: <span className={`font-semibold ${isCorrect ? 'text-emerald-700' : 'text-rose-700'}`}>{userAnswer}</span>
                </p>
              )}
              {!isCorrect && (
                <p className="text-xs text-gray-600">
                  Akzeptiert: <span className="font-semibold text-emerald-700">{correctArr.join(' / ')}</span>
                </p>
              )}
              <ExplanationPanel explanation={q.explanation} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULTS ROUTER ────────────────────────────────────────────────────────────
function HorenResultsView({ part, answers }) {
  const tt = part.taskType;
  const qt = part.questionType;

  // ÖSD B2 Teil 2: the table itself, with every gap marked right/wrong.
  if (tt === 'information-transfer' && part.table) {
    return (
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <InformationTransferTable tableData={part.table} questions={part.questions || []} answers={answers} showResults />
      </div>
    );
  }

  if (qt === 'fill-blank' || tt === 'information-transfer-notes') {
    return <FillBlankResult part={part} answers={answers} />;
  }
  if (tt === 'matching-opinions' || (qt === 'matching' && tt !== 'discussion-comprehension')) {
    return <MatchingResult part={part} answers={answers} />;
  }
  if (tt === 'discussion-comprehension') {
    return <MatchingResult part={part} answers={answers} />;
  }
  // Default: handles true-false, multiple-choice, mixed-true-false-mc
  return <StandardListResult part={part} answers={answers} />;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const LESSON_PREFIX_BY_SECTION = { listening: 'hv' };

function normalizeTrainingSections(rawData) {
  if (Array.isArray(rawData)) return rawData;
  if (Array.isArray(rawData?.sections)) return rawData.sections;
  return [];
}

function extractPartNumber(part, fallbackPartIndex) {
  const byId = String(part?.partId || '').match(/(\d+)(?!.*\d)/);
  if (byId) return parseInt(byId[1], 10);
  const byName = String(part?.partName || '').match(/(?:teil|aufgabe)\s*(\d+)/i);
  if (byName) return parseInt(byName[1], 10);
  return fallbackPartIndex + 1;
}

function buildLessonStrategyLink(provider, level, sectionId, part, partIndex) {
  const prefix = LESSON_PREFIX_BY_SECTION[sectionId];
  if (!prefix) return null;
  const partNumber = extractPartNumber(part, partIndex);
  if (!Number.isFinite(partNumber) || partNumber < 1) return null;
  return {
    dataUrl: `/data/lessons/${provider}-${level}/${prefix}_part${partNumber}.json`,
    badge: `${prefix.toUpperCase()} Teil ${partNumber}`,
  };
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
const HorenTrainingPage = ({ examData, onBack, subscriptionStatus, isLoadingSubscription }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const { examSlug } = useParams();
  const lessonStrategyPath = examSlug ? `/exams/${examSlug}/lesson-strategy` : '/lesson-strategy';

  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [blockedProbeNumber, setBlockedProbeNumber] = useState(null);
  const [pendingExitNav, setPendingExitNav] = useState(null);

  const handleAhaDismiss = () => {
    setShowPremiumModal(false);
    if (typeof pendingExitNav === 'function') pendingExitNav();
    setPendingExitNav(null);
  };
  const handleAhaUpgrade = () => { setShowPremiumModal(false); setPendingExitNav(null); };
  const [resolvedSubscriptionStatus, setResolvedSubscriptionStatus] = useState(subscriptionStatus ?? null);
  const [isResolvingSubscription, setIsResolvingSubscription] = useState(false);

  // Keep local status aligned with parent prop while still allowing a direct refresh in this page.
  useEffect(() => {
    if (subscriptionStatus) {
      setResolvedSubscriptionStatus(subscriptionStatus);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    let cancelled = false;
    const uid = auth.currentUser?.uid;

    if (!uid) {
      return () => {
        cancelled = true;
      };
    }

    const refreshSubscriptionStatus = async () => {
      try {
        setIsResolvingSubscription(true);
        const latestStatus = await subscriptionService.getSubscriptionStatus(uid, { disableSyncWrite: true });
        if (!cancelled) {
          setResolvedSubscriptionStatus(latestStatus);
        }
      } catch (error) {
        console.error('Error refreshing Horen subscription status:', error);
        if (!cancelled) {
          setResolvedSubscriptionStatus(prev => prev || subscriptionStatus || subscriptionService.getDefaultFreeStatus());
        }
      } finally {
        if (!cancelled) {
          setIsResolvingSubscription(false);
        }
      }
    };

    refreshSubscriptionStatus();

    const handleFocus = () => {
      refreshSubscriptionStatus();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleFocus);
    }

    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleFocus);
      }
    };
  }, [subscriptionStatus]);

  const effectiveSubscriptionStatus = resolvedSubscriptionStatus || subscriptionStatus;
  const isSubscriptionLoading = isLoadingSubscription || isResolvingSubscription;

  const isAdmin = adminService.isAdmin();
  const hasPremium = isAdmin || (!isSubscriptionLoading && effectiveSubscriptionStatus && !effectiveSubscriptionStatus.isFree && effectiveSubscriptionStatus.isActive);
  const isFreeModeLockedToFirstProbe = !isSubscriptionLoading && !hasPremium;

  const provider = (examData?.examProvider || 'telc').toLowerCase();
  const level    = (examData?.level || 'B1').toLowerCase();

  // In-progress answers survive refresh / tab close (cleared per part via reset).
  const trainingSessionKey = `logami:v1:training:listening:${auth.currentUser?.uid || 'anon'}:${provider}-${level}`;
  const restoredTraining = useMemo(() => readSession(trainingSessionKey), [trainingSessionKey]);

  const [samples, setSamples]       = useState([]);
  const [sampleIdx, setSampleIdx]   = useState(0);
  const [sampleData, setSampleData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [allAnswers, setAllAnswers]     = useState(() => restoredTraining?.allAnswers ?? {});
  const [checked, setChecked]           = useState(() => restoredTraining?.checked ?? {});
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);

  const formatSampleLabel = (label) =>
    String(label || '').replace(/^Probe\b/i, t('exam.training.probeLabel', { defaultValue: 'Test' }));

  // Audio state for the selected part
  const [audioUrl, setAudioUrl]       = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isMobileAnswerSheetOpen, setIsMobileAnswerSheetOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPartId, setHistoryPartId] = useState(null); // null = all parts
  const [pendingPart, setPendingPart] = useState(null);     // Teil chosen, sample not yet picked
  const [sessionScores, setSessionScores] = useState(() => restoredTraining?.sessionScores ?? {});   // { partKey: {correct,total} } graded at check time
  // Free-plan one-time usage flags (null while loading)
  const [freeAttempts, setFreeAttempts] = useState(null);

  useEffect(() => {
    writeSession(trainingSessionKey, { allAnswers, checked, sessionScores });
  }, [trainingSessionKey, allAnswers, checked, sessionScores]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    freeAttemptService.getFreeAttempts(uid).then(setFreeAttempts);
  }, []);
  const [hasAnswerSheet, setHasAnswerSheet] = useState(false);
  const [partHasAnswerSheet, setPartHasAnswerSheet] = useState(false);
  const [answerSheetOnlyNoTexts, setAnswerSheetOnlyNoTexts] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  const autoOpenedAnswerSheetForPartRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const handle = () => setIsMobile(mq.matches);
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  // Keep free users anchored to Probe 1
  useEffect(() => {
    if (!isFreeModeLockedToFirstProbe) return;
    if (sampleIdx > 0) { setSampleIdx(0); setSelectedPart(null); }
  }, [isFreeModeLockedToFirstProbe, sampleIdx]);

  // Load manifest
  useEffect(() => {
    setLoading(true); setError(null);
    fetch('/data/exam-training/horen/index.json')
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(manifest => { setSamples(manifest?.[provider]?.[level] || []); setSampleIdx(0); })
      .catch(() => { setError('Keine Trainingsdaten gefunden.'); setLoading(false); });
  }, [provider, level]);

  // Load probe data
  useEffect(() => {
    if (!samples.length) { setLoading(false); return; }
    const s = samples[sampleIdx];
    setLoading(true); setError(null);
    fetch(`/data/exam-training/horen/${provider}/${level}/${s.id}.json`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setSampleData(normalizeTrainingSections(data)); setLoading(false); })
      .catch(() => { setError('Datei konnte nicht geladen werden.'); setLoading(false); });
  }, [samples, sampleIdx, provider, level]);

  // Load audio when a part is selected
  useEffect(() => {
    if (!selectedPart || !sampleData) { setAudioUrl(null); return; }
    const probe   = samples[sampleIdx];
    const part    = sampleData[selectedPart.sectionIndex]?.parts?.[selectedPart.partIndex];
    if (!probe || !part) { setAudioUrl(null); return; }
    const partNum = part.partNumber || (selectedPart.partIndex + 1);
    const path    = `audio/horen/${provider}-${level}/${probe.id}/part-${partNum}.mp3`;
    setAudioLoading(true);
    setAudioUrl(null);
    storageService.getAudioUrl(path)
      .then(url => { setAudioUrl(url); })
      .catch(() => { setAudioUrl(null); })
      .finally(() => setAudioLoading(false));
  }, [selectedPart, samples, sampleIdx, provider, level, sampleData]);

  const partKey = selectedPart !== null
    ? `${sampleIdx}-${selectedPart.sectionIndex}-${selectedPart.partIndex}`
    : null;

  const currentPart = selectedPart !== null && sampleData
    ? {
        ...(sampleData[selectedPart.sectionIndex]?.parts?.[selectedPart.partIndex] || {}),
        examProvider: (examData?.examProvider || 'TELC').toUpperCase(),
        level:        (examData?.level || 'B1').toUpperCase(),
      }
    : null;

  const partAnswers = partKey ? (allAnswers[partKey] || {}) : {};
  const isChecked   = partKey ? (checked[partKey]    || false) : false;

  useEffect(() => {
    if (hasAnswerSheet) setPartHasAnswerSheet(true);
  }, [hasAnswerSheet]);

  useEffect(() => {
    if (!partKey) return;
    setIsMobileAnswerSheetOpen(false);
    setHasAnswerSheet(false);
    setPartHasAnswerSheet(false);
    setAnswerSheetOnlyNoTexts(false);
    autoOpenedAnswerSheetForPartRef.current = null;
  }, [partKey]);

  useEffect(() => {
    if (!partKey || !hasAnswerSheet || !answerSheetOnlyNoTexts || !isMobile || isChecked) return;
    if (autoOpenedAnswerSheetForPartRef.current === partKey) return;
    autoOpenedAnswerSheetForPartRef.current = partKey;
    setIsMobileAnswerSheetOpen(true);
  }, [partKey, hasAnswerSheet, answerSheetOnlyNoTexts, isMobile, isChecked]);

  const handleAnswerChange = useCallback((_partId, newAnswers) => {
    if (!partKey) return;
    setAllAnswers(prev => ({ ...prev, [partKey]: newAnswers }));
  }, [partKey]);

  const handleCheckAnswers = useCallback(() => {
    if (!partKey) return;

    // Free plan: one graded attempt per Teil.
    if (isFreeModeLockedToFirstProbe && freeAttemptService.isSkillAttemptUsed(freeAttempts, 'listening', currentPart?.partId)) {
      setShowPremiumModal(true);
      return;
    }

    setIsMobileAnswerSheetOpen(false);
    setChecked(prev => ({ ...prev, [partKey]: true }));

    const uid = auth.currentUser?.uid;
    if (currentPart && selectedPart) {
      const graded = gradeListeningPart(currentPart, partAnswers);
      setSessionScores(prev => ({ ...prev, [partKey]: { correct: graded.correct, total: graded.total } }));
      if (!uid) return;
      if (isFreeModeLockedToFirstProbe && currentPart.partId) {
        freeAttemptService.markSkillAttempt(uid, 'listening', currentPart.partId);
        setFreeAttempts(prev => ({ ...(prev || {}), [`listening_${currentPart.partId}`]: true }));
      }
      const section = sampleData?.[selectedPart.sectionIndex];
      const sample = samples[sampleIdx];
      enhancedUserService.savePracticeAttempt(uid, {
        mode: 'listening',
        provider,
        level,
        sampleId: sample?.id,
        sampleLabel: sample?.label,
        sectionId: section?.sectionId,
        sectionName: section?.sectionName,
        partId: currentPart.partId,
        partNumber: currentPart.partNumber,
        partName: currentPart.partName,
        taskType: currentPart.taskType,
        questionType: currentPart.questionType,
        score: graded.correct,
        maxScore: graded.total,
        userAnswers: Object.fromEntries(graded.results.map(r => [r.questionId, r.userAnswer])),
        correctAnswers: Object.fromEntries(graded.results.map(r => [r.questionId, r.correctAnswer])),
        answers: graded.results,
      });
    }
  }, [partKey, currentPart, partAnswers, sampleData, selectedPart, samples, sampleIdx, provider, level, isFreeModeLockedToFirstProbe, freeAttempts]);

  const handleResetPart = useCallback(() => {
    if (!partKey) return;
    // Free plan: the single attempt for this Teil is used, resetting would only lead to a blocked check.
    if (isFreeModeLockedToFirstProbe && freeAttemptService.isSkillAttemptUsed(freeAttempts, 'listening', currentPart?.partId)) {
      setShowPremiumModal(true);
      return;
    }
    setAllAnswers(prev => ({ ...prev, [partKey]: {} }));
    setChecked(prev => ({ ...prev, [partKey]: false }));
    setAudioIsPlaying(false);
  }, [partKey, isFreeModeLockedToFirstProbe, freeAttempts, currentPart]);

  const toggleFlagQuestion = useCallback((questionId) => {
    setFlaggedQuestions(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  }, []);

  const selectSampleForPendingPart = (i) => {
    if (i < 0 || i >= samples.length || pendingPart === null) return;
    if (isFreeModeLockedToFirstProbe && i > 0) {
      setBlockedProbeNumber(i + 1);
      setShowPremiumModal(true);
      trackPaywallShown({ context: 'horen' });
      return;
    }
    setSampleIdx(i);
    setAudioIsPlaying(false);
    setSelectedPart(pendingPart);
  };

  const handleBack = useCallback(() => {
    if (selectedPart !== null) {
      setIsMobileAnswerSheetOpen(false);
      setAudioIsPlaying(false);
      setSelectedPart(null);
      return;
    }
    const dest = onBack ? () => onBack() : () => navigate('/practice');
    if (isFreeModeLockedToFirstProbe) {
      setPendingExitNav(() => dest);
      setShowPremiumModal(true);
      trackPaywallShown({ context: 'horen' });
      return;
    }
    dest();
  }, [selectedPart, onBack, navigate, isFreeModeLockedToFirstProbe]);

  const openLessonStrategyForPart = useCallback((sectionId, part, partIndex) => {
    const lessonLink = buildLessonStrategyLink(provider, level, sectionId, part, partIndex);
    if (!lessonLink) return;
    navigate(lessonStrategyPath, { state: { dataUrl: lessonLink.dataUrl } });
  }, [provider, level, navigate, lessonStrategyPath]);

  const computeScore = () => {
    if (!currentPart) return { correct: 0, total: 0 };
    return gradeListeningPart(currentPart, partAnswers);
  };

  const hubScore = (sIdx, secIdx, pIdx) => {
    const k    = `${sIdx}-${secIdx}-${pIdx}`;
    const part = sampleData?.[secIdx]?.parts?.[pIdx];
    if (!part || !checked[k]) return null;
    return gradeListeningPart(part, allAnswers[k] || {});
  };

  // ─── LOADING / ERROR ──────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className={`${ELEVATED_SURFACE} p-8 w-full max-w-sm text-center animate-fade-in-up`}>
        <LogamiLogoSpinner size="xl" className="mx-auto" decorative={false} alt="Logami loading" />
        <p className="mt-4 text-sm text-gray-600 font-semibold">Trainingsdaten werden geladen ...</p>
      </div>
    </div>
  );

  if (error || !samples.length) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className={`${ELEVATED_SURFACE} text-center max-w-sm w-full p-6 animate-fade-in-up`}>
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-semibold mb-2">{error || 'Noch keine Trainingsaufgaben verfügbar.'}</p>
        <p className="text-gray-400 text-sm mb-6">
          Für {provider.toUpperCase()} {level.toUpperCase()} gibt es noch keine Hören-Trainingsdaten.
        </p>
        <button
          onClick={() => navigate('/practice')}
          className="min-h-[48px] inline-flex items-center justify-center px-4 py-2 rounded-xl border-2 border-black bg-black text-white font-bold text-sm hover:bg-gray-900 touch-manipulation transition-all duration-300"
        >
          {t('common.goBack')}
        </button>
      </div>
    </div>
  );

  // ─── PART VIEW ────────────────────────────────────────────────────────────────
  if (showHistory) {
    return (
      <PracticeHistoryPanel
        mode="listening"
        provider={provider}
        level={level}
        onClose={() => setShowHistory(false)}
        filterAttempt={historyPartId ? (attempt) => attempt.partId === historyPartId : undefined}
      />
    );
  }

  // ─── SAMPLE SELECT (Teil chosen, Muster not yet) ─────────────────────────────
  if (pendingPart !== null && selectedPart === null) {
    const pendingPartData = sampleData?.[pendingPart.sectionIndex]?.parts?.[pendingPart.partIndex];
    return (
      <>
      <AhaUpsellModal
        open={showPremiumModal}
        onDismiss={handleAhaDismiss}
        onUpgrade={handleAhaUpgrade}
        context="horen"
      />
      <SampleSelectView
        partName={pendingPartData?.partName || `Teil ${pendingPart.partIndex + 1}`}
        partTypeLabel={PART_TYPE_LABELS[pendingPartData?.taskType] || pendingPartData?.taskType}
        samples={samples}
        formatLabel={formatSampleLabel}
        isSampleLocked={(i) => isFreeModeLockedToFirstProbe && i > 0}
        getSessionScore={(i) => sessionScores[`${i}-${pendingPart.sectionIndex}-${pendingPart.partIndex}`] || null}
        getSessionProgress={(i) => {
          const k = `${i}-${pendingPart.sectionIndex}-${pendingPart.partIndex}`;
          if (checked[k]) return null;
          const answered = Object.values(allAnswers[k] || {}).filter(v => v !== null && v !== undefined && v !== '').length;
          return answered > 0 ? answered : null;
        }}
        onSelectSample={selectSampleForPendingPart}
        onBack={() => setPendingPart(null)}
        onOpenHistory={() => { setHistoryPartId(pendingPartData?.partId || null); setShowHistory(true); }}
      />
      </>
    );
  }

  if (selectedPart !== null && currentPart) {
    const currentSection = sampleData?.[selectedPart.sectionIndex];
    const currentLessonLink = buildLessonStrategyLink(
      provider, level, currentSection?.sectionId, currentPart, selectedPart.partIndex
    );

    const score         = computeScore();
    const totalQ        = (currentPart.questions || []).filter(q => !q.isExample).length;
    const answeredCount = Object.values(partAnswers).filter(v => v !== null && v !== undefined && v !== '').length;
    const answeredPct   = totalQ > 0 ? Math.round((answeredCount / totalQ) * 100) : 0;
    const pct           = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;
    const scoreColor    = pct >= 80 ? 'text-emerald-700 bg-emerald-100 border-emerald-300'
                        : pct >= 60 ? 'text-amber-700 bg-amber-100 border-amber-300'
                        :             'text-rose-700 bg-rose-100 border-rose-300';

    // Compute directly from task type, avoids React dev/prod callback-timing divergence
    const HOREN_ANSWER_SHEET_TYPES = new Set([
      'short-texts', 'short-texts-announcements', 'short-texts-opinion',
      'conversation', 'conversation-comprehension', 'radio-interview',
      'news-broadcast', 'monologue-comprehension', 'discussion-comprehension',
      'matching-opinions', 'information-transfer-notes', 'fill-blank',
    ]);
    const showAnswerSheetButton = !isChecked && HOREN_ANSWER_SHEET_TYPES.has(currentPart.taskType);

    const playCount = currentPart.audio?.playCount || 1;

    return (
      <div className="min-h-screen bg-white pb-16 sm:pb-20 animate-fade-in">
        <AhaUpsellModal
          open={showPremiumModal}
          onDismiss={handleAhaDismiss}
          onUpgrade={handleAhaUpgrade}
          context="horen"
        />

        {/* Sticky header */}
        <div className="sticky top-0 z-30 bg-white/92 backdrop-blur-xl border-b border-black/10 shadow-sm safe-area-inset-top">
          <div className="w-full px-4 sm:px-6 pt-3 pb-3 sm:py-4">
            <div className="flex items-center justify-between lg:justify-start w-full gap-4 flex-wrap lg:flex-nowrap">

              {/* LEFT: back + badge + title */}
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="hidden sm:block">
                  <AnimatedBackButton onClick={handleBack} backLabel={t('common.goBack')} />
                </div>
                <div className="min-w-0 lg:max-w-[280px]">
                  <p className="inline-flex max-w-full px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] text-[#3A86FF] bg-[#3A86FF]/10 border border-[#3A86FF]/20 leading-tight truncate">
                    {currentSection?.sectionName}
                  </p>
                  <p className="font-space font-black text-gray-900 text-sm sm:text-base truncate mt-1">{currentPart.partName}</p>
                </div>
              </div>

              {/* RIGHT: progress + controls (desktop) */}
              <div className="hidden lg:flex min-w-0 flex-1 items-center gap-3">
                <div className={`${SOFT_SURFACE} flex items-center gap-3 px-3 py-2.5 min-w-[300px]`}>
                  {isChecked ? (
                    <>
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-bold text-sm flex-shrink-0 ${scoreColor}`}>
                        <CheckCircle className="w-4 h-4" />
                        {score.correct}/{score.total} · {pct}%
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black transition-all duration-500" style={{ width: '100%' }} />
                        </div>
                      </div>
                      <button onClick={handleResetPart} className="flex-shrink-0 min-h-[44px] inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 border-black/10 bg-white text-gray-700 font-bold text-sm hover:border-black/30 hover:bg-gray-50 touch-manipulation transition-all duration-300">
                        <RotateCcw className="w-4 h-4" /> Nochmal
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="min-w-[112px] flex-shrink-0 text-center">
                        <p className="text-sm text-gray-600 leading-tight">
                          <span className="font-bold text-gray-900">{answeredCount}</span>/{totalQ} beantwortet
                        </p>
                        <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold mt-1">Fortschritt {answeredPct}%</p>
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black transition-all duration-500" style={{ width: `${answeredPct}%` }} />
                        </div>
                      </div>
                      <button
                        onClick={handleCheckAnswers}
                        disabled={answeredCount === 0}
                        className="flex-shrink-0 min-h-[44px] inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-black via-gray-900 to-[#1f3b63] text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_8px_20px_rgba(17,24,39,0.28)] active:scale-[0.98] touch-manipulation transition-all duration-300"
                      >
                        <CheckCircle className="w-4 h-4" /> Antworten prüfen
                      </button>
                    </>
                  )}
                </div>

                <div className="ml-auto flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={() => setTranscriptOpen(true)}
                    className="flex-shrink-0 min-h-[48px] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-black/10 bg-white text-gray-700 font-semibold text-sm hover:border-[#3A86FF]/40 hover:bg-[#3A86FF]/5 hover:text-[#275eb4] transition-all duration-300 touch-manipulation whitespace-nowrap"
                    aria-label="Transkript anzeigen"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Transkript</span>
                  </button>
                  {currentLessonLink && (
                    <button
                      onClick={() => openLessonStrategyForPart(currentSection?.sectionId, currentPart, selectedPart.partIndex)}
                      className="flex-shrink-0 min-h-[48px] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#1f3b63]/20 bg-[#eef3fb] text-[#1f3b63] font-semibold text-sm hover:bg-[#e4edf9] hover:border-[#1f3b63]/35 transition-all duration-300 touch-manipulation whitespace-nowrap"
                      aria-label={t('nav.lessons')}
                    >
                      <Lightbulb className="w-4 h-4" />
                      <span>{t('nav.lessons')}</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile progress bar */}
            <div className={`${SOFT_SURFACE} mt-3 px-3 py-3 sm:px-4 lg:hidden`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {isChecked ? (
                  <>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-bold text-sm sm:flex-shrink-0 ${scoreColor}`}>
                      <CheckCircle className="w-4 h-4" />
                      {score.correct}/{score.total} · {pct}%
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black" style={{ width: '100%' }} />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center sm:min-w-[140px] sm:flex-shrink-0">
                      <p className="text-sm text-gray-600">
                        <span className="font-bold text-gray-900">{answeredCount}</span>/{totalQ} beantwortet
                      </p>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold">Fortschritt {answeredPct}%</p>
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black transition-all duration-500" style={{ width: `${answeredPct}%` }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Audio player + task content */}
        <div className="pt-1">
          {/* Audio player */}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5 pb-2">
            {audioLoading ? (
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-dashed border-gray-300 bg-gray-50">
                <LogamiLogoSpinner size="sm" className="flex-shrink-0" decorative={false} alt="Logami loading" />
                <p className="text-xs text-gray-500 font-medium">Audio wird geladen ...</p>
              </div>
            ) : (
              <PracticeAudioPlayer ref={audioPlayerRef} audioUrl={audioUrl} playCount={playCount} onPlayingStateChange={setAudioIsPlaying} />
            )}
          </div>

          {/* Questions */}
          {isChecked ? (
            <HorenResultsView part={currentPart} answers={partAnswers} />
          ) : (
            <div className="animate-fade-in-up">
              <PartDisplay
                partData={currentPart}
                savedAnswers={partAnswers}
                onAnswerChange={handleAnswerChange}
                flaggedQuestions={flaggedQuestions}
                toggleFlagQuestion={toggleFlagQuestion}
                goToNextPart={() => {}}
                allParts={[]}
                timeRemaining={undefined}
                currentSection={sampleData?.[selectedPart.sectionIndex]}
                isWarning={false}
                isCritical={false}
                isMobileAnswerSheetOpen={isMobileAnswerSheetOpen}
                onCloseMobileAnswerSheet={() => setIsMobileAnswerSheetOpen(false)}
                onHasAnswerSheetChange={setHasAnswerSheet}
                onAnswerSheetOnlyNoTexts={setAnswerSheetOnlyNoTexts}
              />
            </div>
          )}
        </div>

        {/* Bottom Navigation. Exercise View (mobile only) */}
        <GrammarBottomNav
          buttons={[
            { id: 'back',  icon: ArrowLeft,  label: t('grammar.back', { defaultValue: 'Back' }), onClick: handleBack, color: '#3A86FF' },
            { id: 'check', icon: CheckCircle, label: 'Antworten prüfen', onClick: handleCheckAnswers, disabled: isChecked || answeredCount === 0, active: !isChecked, color: '#3A86FF' },
            { id: 'transcript', icon: FileText, label: 'Transkript', onClick: () => setTranscriptOpen(true), color: '#3A86FF' },
            showAnswerSheetButton ? {
              id: 'answer-sheet',
              icon: ClipboardList,
              label: t('home.timer.answerSheet', { defaultValue: 'Answer sheet' }),
              onClick: () => setIsMobileAnswerSheetOpen(true),
              color: '#006D77',
              iconColor: '#006D77',
              labelColor: '#006D77',
              iconContainerStyle: { backgroundColor: 'rgba(0,109,119,0.1)' },
              showActiveIndicator: false,
            } : null,
            { id: 'start', icon: audioIsPlaying ? Pause : Play, label: audioIsPlaying ? 'Pause' : t('button.start', { defaultValue: 'Start' }), onClick: () => audioPlayerRef.current?.togglePlay(), color: '#3A86FF' },
            { id: 'reset', icon: RotateCcw, label: t('button.reset', { defaultValue: 'Reset' }), onClick: () => { handleResetPart(); audioPlayerRef.current?.reset(); }, color: '#3A86FF' },
          ].filter(Boolean)}
        />

        {/* Transcript drawer: slides in from the right (full-width on mobile), player + questions stay underneath */}
        <SlideOutPanel isOpen={transcriptOpen} onClose={() => setTranscriptOpen(false)} title="Transkript">
          <HorenTranscriptBody part={currentPart} />
        </SlideOutPanel>
      </div>
    );
  }

  // ─── HUB VIEW ─────────────────────────────────────────────────────────────────
  const listeningSections = (sampleData || []).filter(s => s.sectionId === 'listening');

  const renderPartCard = (sectionId, sectionIndex, part, partIndex) => {
    const k        = `${sampleIdx}-${sectionIndex}-${partIndex}`;
    const answered = Object.keys(allAnswers[k] || {}).length;
    const total    = (part.questions || []).filter(q => !q.isExample).length;
    const isChkd   = checked[k] || false;
    const sc       = hubScore(sampleIdx, sectionIndex, partIndex);
    const lessonLink = buildLessonStrategyLink(provider, level, sectionId, part, partIndex);
    const playCount  = part.audio?.playCount || 1;

    const scoreTextColor = sc?.correct === sc?.total ? 'text-emerald-700'
      : sc?.correct >= sc?.total * 0.6 ? 'text-amber-700' : 'text-rose-700';

    const openPart = () => {
      // Sample (Muster) is picked on the next screen; the paywall check for
      // locked samples happens there in selectSampleForPendingPart.
      setPendingPart({ sectionIndex, partIndex });
    };

    const onCardKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPart(); }
    };

    return (
      <div
        key={partIndex}
        role="button"
        tabIndex={0}
        onClick={openPart}
        onKeyDown={onCardKeyDown}
        className="horen-task-card group relative w-full overflow-hidden rounded-2xl border-2 border-black/10 bg-white p-3.5 hover:border-[#3A86FF]/38 hover:shadow-[0_18px_34px_rgba(58,134,255,0.14)] hover:-translate-y-1 active:scale-[0.995] transition-all duration-300 text-left touch-manipulation cursor-pointer animate-fade-in-up focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/30"
        style={{ animationDelay: `${partIndex * 70}ms` }}
        aria-label={t('exam.training.start', { defaultValue: 'Start training' })}
      >
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#3A86FF]/10 via-white to-[#0F172A]/[0.03] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="relative z-10 flex items-start gap-3.5">
          <AnimatedTaskTypeIcon taskType={part.taskType} />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-gray-900 text-sm sm:text-[15px] leading-tight">{part.partName || `Teil ${partIndex + 1}`}</p>
                <p className="mt-0.5 text-[11px] text-gray-500">{PART_TYPE_LABELS[part.taskType] || part.taskType}</p>
              </div>
              {isChkd && sc && (
                <span className={`hidden sm:inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${scoreTextColor} ${sc.correct === sc.total ? 'bg-emerald-50 border-emerald-200' : sc.correct >= sc.total * 0.6 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'}`}>
                  {sc.correct}/{sc.total}
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-black/10 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                {total} Aufgaben · {part.points} Pkt.
              </span>
              {/* Audio play count badge */}
              <span className="inline-flex items-center gap-1 rounded-full border border-[#3A86FF]/20 bg-[#3A86FF]/8 px-2.5 py-1 text-[11px] font-semibold text-[#275eb4]">
                <Volume2 className="w-3 h-3" />
                {playCount}× hören
              </span>
              {answered > 0 && !isChkd && (
                <span className="inline-flex items-center rounded-full border border-[#3A86FF]/18 bg-[#3A86FF]/8 px-2.5 py-1 text-[11px] font-semibold text-[#275eb4]">
                  {answered}/{total} beantwortet
                </span>
              )}
              {isChkd && sc && (
                <span className={`inline-flex sm:hidden items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${scoreTextColor} ${sc.correct === sc.total ? 'bg-emerald-50 border-emerald-200' : sc.correct >= sc.total * 0.6 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'}`}>
                  {sc.correct}/{sc.total} richtig
                </span>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-black text-white bg-gradient-to-r from-black via-[#0F172A] to-[#1F3B63] shadow-[0_10px_22px_rgba(15,23,42,0.2)]">
                {t('exam.training.start', { defaultValue: 'Start training' })}
                <ChevronRight className="horen-task-icon-arrow h-3.5 w-3.5" />
              </span>

              {lessonLink && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openLessonStrategyForPart(sectionId, part, partIndex); }}
                  className="min-h-[34px] inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-gray-500 transition-colors duration-200 hover:border-black/20 hover:text-gray-700 touch-manipulation"
                  aria-label={t('nav.lessons')}
                >
                  <Lightbulb className="h-3.5 w-3.5" />
                  <span>{t('nav.lessons')}</span>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-10 animate-fade-in">
      <style>{HOREN_TRAINING_CARD_STYLES}</style>
      <div className="max-w-4xl mx-auto px-4 py-5 sm:py-8">

        {/* Header */}
        <div className={`${ELEVATED_SURFACE} p-3.5 sm:p-[18px] mb-5 sm:mb-6 animate-fade-in-up`}>
          <div className="grid grid-cols-[auto_1fr_auto] items-start sm:items-center gap-3 sm:gap-4">
            <div className="justify-self-start hidden sm:block">
              <AnimatedBackButton onClick={handleBack} backLabel={t('common.goBack')} />
            </div>

            <div className="text-center">
              <div className="inline-block px-3 py-1 bg-black text-white text-[11px] font-bold tracking-widest uppercase rounded-full mb-1 animate-fade-in-up">
                Hören Training
              </div>
              <h1 className="font-space text-2xl sm:text-4xl font-black text-gray-900 leading-tight">
                {(examData?.examProvider || 'TELC').toUpperCase()} {(examData?.level || 'B1').toUpperCase()}
              </h1>
            </div>

            <div className="hidden sm:block" />
          </div>

        </div>

        {/* Part cards */}
        <div className="space-y-5">
          {listeningSections.map((section, sectionLocalIdx) => {
            const sectionIndex = sampleData.indexOf(section);
            return (
              <div key={`${section.sectionId}-${sectionLocalIdx}`} className={`${ELEVATED_SURFACE} relative p-3.5 sm:p-4 animate-fade-in-up`}>
                <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl bg-gradient-to-r from-transparent via-[#3A86FF]/35 to-transparent" />

                <div className="relative z-10 flex items-center gap-3 mb-3.5">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#3A86FF]/35 to-transparent" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-gray-500 px-2">{section.sectionName}</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#3A86FF]/35 to-transparent" />
                </div>

                <div className="relative z-10 space-y-2.5">
                  {(section.parts || []).map((part, partIndex) =>
                    renderPartCard(section.sectionId, sectionIndex, part, partIndex)
                  )}
                </div>

                <div className="relative z-10 mt-3.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3A86FF]" />
                  {t('exam.training.partsCount', { count: section.parts?.length || 0, defaultValue: '{{count}} parts' })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Navigation. Hub View (mobile only) */}
      <GrammarBottomNav
        buttons={[
          { id: 'back',     icon: ArrowLeft,  label: t('grammar.back', { defaultValue: 'Back' }), onClick: handleBack, color: '#3A86FF' },
          { id: 'practice', icon: Headphones, label: t('exam.training.listeningNav', { defaultValue: 'Listening' }), onClick: () => { if (isFreeModeLockedToFirstProbe) { setPendingExitNav(() => () => navigate('/practice')); setShowPremiumModal(true); trackPaywallShown({ context: 'horen' }); } else navigate('/practice'); }, active: true, color: '#3A86FF' },
          { id: 'home',     icon: Home,        label: t('nav.home', { defaultValue: 'Home' }),    onClick: () => { if (isFreeModeLockedToFirstProbe) { setPendingExitNav(() => () => navigate('/dashboard')); setShowPremiumModal(true); trackPaywallShown({ context: 'horen' }); } else navigate('/dashboard'); }, color: '#3A86FF' },
        ]}
      />

      {/* Premium Block Modal */}
      <AhaUpsellModal
        open={showPremiumModal}
        onDismiss={handleAhaDismiss}
        onUpgrade={handleAhaUpgrade}
        context="horen"
      />
    </div>
  );
};

export default HorenTrainingPage;
